import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Validators, ValidatorFn, FormControl } from '@angular/forms';
import {
  ReportTemplate, ReportTemplateUpdate, TemplateDefinition, TemplateCommonSection, TemplateText, TemplatePageSection,
  ReportTemplateImageList, ResponseIdList, UpdateResultList, UpdateIdList, ReportTitleFormat
} from '../_offline/models/report-preview/report-template';
import {
  ReportProperties,
  ReportPage,
  ReportText,
  ReportLine,
  ReportImage,
  ReportRectangle,
  ReportModel,
  Location,
  ElementType,
  FromToDetail,
  FromTo,
  ReportSummary,
  ReportElement,
  ReportDataPlateOffline
} from '../_objects/report-element';

import { ReportTemplateService } from './http-api/report-template.service';
import { REGULAR_EXPRESSION } from '../app.constants';

export interface Notifier {
  componentName: string;
  ids: NotifierIds;
}

export interface NotifierIds {
  pageId: string;
  elementId: string;
}

export type FieldType = 'ReadOnly' | 'Text' | 'TextArea' | 'Number' | 'Integer' | 'DateTime' | 'CheckBox' | 'Select' | 'JSON' | 'Multi';
export type TypeOfJSON = string | number | boolean | object;

export interface PropertyPreviewMetadata {
  name: string;
  parentName: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  isRequired: boolean;
  isEdited: boolean;
  selectItems: { label: string, value?: string }[];
  isFieldType(type: FieldType): boolean;
}

export interface ReportPreviewMetadata {
  topPx: number;
  leftPx: number;
  widthPx: number;
  heightPx: number;
}

export interface UpdateMetadata {
  id: string;
  name: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  oldValue: TypeOfJSON | Array<TypeOfJSON>;
}

export interface MousePoint {
  onElementX: number;
  onElementY: number;
}

export class JSONSyntaxValidator {
  static invalidJson(jsonFormCtrl: FormControl<string>) {
    if (jsonFormCtrl.value) {
      try {
        JSON.parse(jsonFormCtrl.value);
      } catch (err) {
        return { syntax: true };
      }
    }
    return null;
  }
}

/**
 * レポートテンプレートのデザインに関するServiceクラス
 */
@Injectable()
export class ReportTemplateDesignService {

  private templateId: string;

  private originTemplate: ReportTemplate;
  private tempTemplate: ReportTemplate;
  private reportModel: ReportModel;
  private templateImages: ReportTemplateImageList[];
  private viewPageId: string;

  private onReady$: Subject<string>;
  public onReady: Observable<string>;

  private onSelectedElement$: Subject<string>;
  public onSelectedElement: Observable<string>;

  private onEndDraw$: Subject<string>;
  public onEndDraw: Observable<string>;

  private onEndUpdate$: Subject<string>;
  public onEndUpdate: Observable<string>;

  private readonly virtualGridPx: number;

  private drawElementType: ElementType;
  private dragElements: {
    [id: string]: {
      type: ElementType,
      from: FromToDetail,
      dragPoint: MousePoint
    }
  };
  private resizeElements: {
    [id: string]: {
      type: ElementType,
      markers: {
        horizontal: string,
        vertical: string
      },
      beginPoint: MousePoint
    }
  };

  get reportProperties():  ReportElement[] {
    if (!this.reportModel) {return null; }

    const result = Object.entries(this.reportModel.ElementFinder);
    return result.map(_ => _[1]);
  }

  constructor(
    private reportTemplateService: ReportTemplateService) {

    this.onReady$ = new Subject();
    this.onReady = this.onReady$.asObservable();

    this.onSelectedElement$ = new Subject();
    this.onSelectedElement = this.onSelectedElement$.asObservable();

    this.onEndDraw$ = new Subject();
    this.onEndDraw = this.onEndDraw$.asObservable();

    this.onEndUpdate$ = new Subject();
    this.onEndUpdate = this.onEndUpdate$.asObservable();

    this.virtualGridPx = 15; // 仮値。
  }

  public initialize(reportTemplateId: string) {

    this.templateId = reportTemplateId;

    this.reportTemplateService.getReportTemplate(this.templateId)
      .subscribe((template: ReportTemplate) => {
        this.loadTemplate(template);
      });
  }
  public loadTemplate(template: ReportTemplate) {
    this.tempTemplate = template;
    this.originTemplate = JSON.parse(JSON.stringify(template, undefined, 4));
    this.reportModel = new ReportModel(template.template);
    this.viewPageId = null;
    this.onEndUpdate$.next('Updated');
    this.loadReportTemplateImages();
  }
  public reloadTemplate(template: ReportTemplate) {
    // this.reportModel.removeAllPage();
    this.loadTemplate(template);
  }

  public getOriginTemplate(): ReportTemplate {
    return this.originTemplate;
  }
  public getTemplateId(): string {
    return this.tempTemplate.id;
  }
  public getTemplateName(): string {
    return this.tempTemplate.name;
  }
  public getTemplateIsValid(): boolean {
    return this.tempTemplate.isValid;
  }
  public getTemplateReportTitleFormat(): ReportTitleFormat[] {
    return this.tempTemplate.reportTitleFormat;
  }
  public getTemplateReportTitleInputMode(): string {
    return this.tempTemplate.reportTitleInputMode;
  }

  public getTemplateSubjectModelIds(): string[] {
    return this.tempTemplate.subjectModelIds;
  }
  public setTemplateName(name: string) {
    this.tempTemplate.name = name;
  }
  public setTemplateIsValid(isValid: boolean) {
    this.tempTemplate.isValid = isValid;
  }
  public setTemplateReportTitleFormat(reportTitleFormat: ReportTitleFormat[]) {
    this.tempTemplate.reportTitleFormat = reportTitleFormat;
  }
  public setTemplateReportTitleInputMode(reportTitleInputMode: string) {
    this.tempTemplate.reportTitleInputMode = reportTitleInputMode;
  }
  public setTemplateSubjectModelIds(subjectModelIds: string[]) {
    this.tempTemplate.subjectModelIds = subjectModelIds;
  }
  public isChangeTemplate(): boolean {
    if (this.originTemplate && this.tempTemplate) {
      const originJson = JSON.stringify(this.originTemplate.template, undefined, 4);
      const tempJson = JSON.stringify(this.getTemplate(), undefined, 4);
      return originJson !== tempJson || this.originTemplate.name !== this.tempTemplate.name
        || this.originTemplate.isValid !== this.tempTemplate.isValid
        || this.originTemplate.reportTitleInputMode !== this.tempTemplate.reportTitleInputMode
        || JSON.stringify(this.originTemplate.reportTitleFormat, undefined, 4) !==
        JSON.stringify(this.tempTemplate.reportTitleFormat, undefined, 4);
    } else {
      return false;
    }
  }
  public isValidationError(): boolean {
    let isError = false;
    const template = this.getTemplate();
    if (template.header && template.header.texts) {
      template.header.texts.forEach((text: TemplateText) => {
        if (text.inputType === 'select') {
          if (!text.options || text.options.length === 0 || text.options.some(opt => opt.label.length < 1 || opt.value.length < 1)) {
            isError = true;
          }
        }
        if (text.type === 'dynamic-editable' && !text.editableId) {
          isError = true;
        }
        // ToDo v2.0.2以降に対応
        /* if (text.type === 'dynamic-editable' && text.editableId && !this.isLowerCamelCase(text.editableId)) {
          isError = true;
        } */
        if (text.type === 'dynamic' && !text.dynamicId) {
          isError = true;
        }
      });
    }
    if (template.bodies) {
      template.bodies.forEach((body: TemplatePageSection) => {
        body.texts.forEach((text: TemplateText) => {
          if (text.inputType === 'select') {
            if (!text.options || text.options.length === 0 || text.options.some(opt => opt.label.length < 1 || opt.value.length < 1)) {
              isError = true;
            }
          }
          if (text.type === 'dynamic-editable' && !text.editableId) {
            isError = true;
          }
          // ToDo v2.0.2以降に対応
          /* if (text.type === 'dynamic-editable' && text.editableId && !this.isLowerCamelCase(text.editableId)) {
            isError = true;
          } */
          if (text.type === 'dynamic' && !text.dynamicId) {
            isError = true;
          }
        });
      });
    }
    if (template.footer && template.footer.texts) {
      template.footer.texts.forEach((text: TemplateText) => {
        if (text.inputType === 'select') {
          if (!text.options || text.options.length === 0 || text.options.some(opt => opt.label.length < 1 || opt.value.length < 1)) {
            isError = true;
          }
        }
        if (text.type === 'dynamic-editable' && !text.editableId) {
          isError = true;
        }
        // ToDo v2.0.2以降に対応
        /* if (text.type === 'dynamic-editable' && text.editableId && !this.isLowerCamelCase(text.editableId)) {
          isError = true;
        } */
        if (text.type === 'dynamic' && !text.dynamicId) {
          isError = true;
        }
      });
    }
    return isError;
  }

  // editableIdの入力文字チェック
  isLowerCamelCase(textField): boolean {
    return REGULAR_EXPRESSION.SMALL_LETTERS_PATTERN.test(textField[0]);
  }

  // FieldType:Text
  public validatorText(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:Number
  public validatorNumber(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(Validators.pattern(REGULAR_EXPRESSION.DECIMAL_PATTERN));
    return validator;
  }

  // FieldType:Integer
  public validatorInteger(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN));
    return validator;
  }

  // FieldType:TextArea
  public validatorTextArea(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:JSON
  public validatorJSON(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Multi
  public validatorMulti(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Select
  public validatorSelect(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    return validator;
  }

  // FieldType:CheckBox
  public validatorCheckBox(): Array<ValidatorFn> {
    return null;
  }

  public loadReportTemplateImages() {
    this.reportTemplateService.getReportTemplateImages(this.templateId)
      .subscribe((images: ReportTemplateImageList[]) => {
        this.templateImages = images;
        this.onReady$.next('I am ready');
      });
  }
  public getTargetIdList(): Observable<ResponseIdList[]> {
    return this.reportTemplateService.getModelsForTemplates(this.templateId, null);
  }
  public updateModelsForTemplates(updateIdList: UpdateIdList): Observable<UpdateResultList> {
    return this.reportTemplateService.updateModelsForTemplates(this.templateId, updateIdList);
  }

  public getReportTemplateImages(): { label: string, value?: string }[] {
    const tempImages: { label: string, value?: string }[] = [];
    this.templateImages.forEach((image: ReportTemplateImageList) => {
      tempImages.push({ label: image.fileName, value: image.fileName });
    });
    return tempImages;
  }
  public getImageUrl(fileName: string): string {
    let imageURL: string;
    this.templateImages.forEach((image: ReportTemplateImageList) => {
      if (fileName === image.fileName) {
        imageURL = image.originalUrl;
      }
    });
    return imageURL;
  }

  public getElementType(): ElementType {
    return this.drawElementType;
  }
  public notifySelectElement(notifier: Notifier, targetId: string, isCtrl: boolean) {
    this.reportModel.selectElement(targetId, isCtrl);
    this.onSelectedElement$.next('');
  }
  public notifyChangeScroll(notifier: Notifier, targetId: string) {
    if (targetId !== this.viewPageId) {
      this.reportModel.changePage(targetId);
      this.onSelectedElement$.next('');
      this.viewPageId = targetId;
    }
  }

  public notifyDeselectAll(notifier: Notifier, isCtrl: boolean) {
    this.reportModel.deselectAll(isCtrl);
    this.onSelectedElement$.next('');
  }

  public notifyAddPage(notifier: Notifier): void {
    this.reportModel.addPage();
    this.onSelectedElement$.next('');
  }

  public notifyDrawMode(notifier: Notifier, drawType: ElementType): void {
    this.drawElementType = drawType;
    this.reportModel.beginDrawMode();
  }

  public notifyDraw(notifier: Notifier, location: Location, metadata: ReportPreviewMetadata): void {
    const pageId = (location === Location.Body) ? notifier.ids.pageId : undefined;
    metadata = this.fitVirtualGrid(metadata); // 仮想Gridにfitする座標値になるよう再計算する
    this.reportModel.drawElement(location, this.drawElementType, metadata, pageId);
    this.reportModel.endDrawMode();
    this.onEndDraw$.next('');
    this.onSelectedElement$.next('');
    delete this.drawElementType;
  }

  public notifyDrawCancel(notifier: Notifier) {
    this.reportModel.endDrawMode();
    this.onEndDraw$.next('');
    delete this.drawElementType;
  }

  public notifyBeginDrag(notifier: Notifier, dragElementType: ElementType, fromDetail: FromToDetail, dragMousePoint: MousePoint) {
    if (!this.dragElements) {
      this.dragElements = {};
    }
    this.dragElements[notifier.ids.elementId] = {
      type: dragElementType,
      from: fromDetail,
      dragPoint: dragMousePoint
    };
  }

  public notifyDrop(notifier: Notifier, dragElemId: string, toDetail: FromToDetail, dropMousePoint: MousePoint) {
    const dragElement = this.dragElements[dragElemId];

    // 仮想Gridにfitする座標値でmetadata生成
    const dropMetadata: ReportPreviewMetadata = this.fitVirtualGrid({
      topPx: dropMousePoint.onElementY - dragElement.dragPoint.onElementY,
      leftPx: dropMousePoint.onElementX - dragElement.dragPoint.onElementX,
      widthPx: 0, // ドロップ操作でwidthは変更できないのでダミー値を設定
      heightPx: 0, // ドロップ操作でheightは変更できないのでダミー値を設定
    });

    const fromTo: FromTo = {
      from: dragElement.from,
      to: toDetail
    };

    this.reportModel.dropElement(dragElemId, dragElement.type, dropMetadata, fromTo);
    delete this.dragElements[dragElemId];
  }

  public notifyBeginResize(notifier: Notifier, type: ElementType, grabPointH: string, grabPointV: string, beginPoint: MousePoint) {
    if (!this.resizeElements) {
      this.resizeElements = {};
    }
    this.resizeElements[notifier.ids.elementId] = {
      type: type,
      markers: {
        horizontal: grabPointH,
        vertical: grabPointV
      },
      beginPoint: beginPoint
    };
  }

  public notifyResize(notifier: Notifier, resizePoint: MousePoint) {

    const resizeBegin = this.resizeElements[notifier.ids.elementId].beginPoint;
    const markers = this.resizeElements[notifier.ids.elementId].markers;

    const resizeOffset: ReportPreviewMetadata = this.fitVirtualGrid({
      topPx: resizePoint.onElementY - resizeBegin.onElementY,
      leftPx: resizePoint.onElementX - resizeBegin.onElementX,
      widthPx: resizePoint.onElementX - resizeBegin.onElementX,
      heightPx: resizePoint.onElementY - resizeBegin.onElementY
    });

    this.reportModel.resizeElement(notifier.ids.elementId, resizeOffset, markers.horizontal, markers.vertical);
  }

  public notifyDelete(notifier: Notifier): void {
    this.notifyDrawCancel(notifier);
    this.reportModel.deleteSelected();
  }

  public notifyUpdateCommonProperties(notifier: Notifier, updateMetadata: UpdateMetadata) {
    this.reportModel.updateCommonProperties(updateMetadata);
  }

  public notifyUpdateElementProperties(notifier: Notifier, updateMetadata: UpdateMetadata) {
    this.reportModel.updateElementProperties(updateMetadata);
  }

  public fitVirtualGrid(src: ReportPreviewMetadata): ReportPreviewMetadata {

    const calcGridPx = (value: number): number => {
      const useMinus: boolean = (value < 0);
      const mod = Math.abs(value) % this.virtualGridPx;
      let rtnValue = Math.abs(value);

      rtnValue = (mod < (this.virtualGridPx / 2)) ? rtnValue - mod : rtnValue + (this.virtualGridPx - mod);
      rtnValue = (useMinus && rtnValue !== 0) ? rtnValue * -1 : rtnValue;

      // console.log('calc log: %s -> %s (mod: %s, under: %s, over: %s)',
      //   value,
      //   rtnValue,
      //   mod,
      //   Math.abs(value) - mod,
      //   Math.abs(value) + (this.virtualGridPx - mod)
      // );

      return rtnValue;
    };
    src.heightPx = calcGridPx(src.heightPx);
    src.widthPx = calcGridPx(src.widthPx);
    src.topPx = calcGridPx(src.topPx);
    src.leftPx = calcGridPx(src.leftPx);

    return src;
  }

  public getCommonProperties(): ReportProperties[] {
    return this.reportModel.getCommonProperties();
  }

  public getSelectedElementProperties(): ReportProperties[] {
    return this.reportModel.getSelectedElementProperties();
  }

  public getPageIds(): string[] {
    return this.reportModel.getPageIds();
  }
  public getTextIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Text');
  }
  public getLineIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Line');
  }
  public getImageIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Image');
  }
  public getRectangleIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Rectangle');
  }
  public getSummaryIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Summary');
  }
  public getPictureIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Picture');
  }
  public getDataPlateIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'DataPlate');
  }

  public getPage(pageId: string): ReportPage {
    return this.reportModel.getPage(pageId);
  }
  public getText(textId: string): ReportText {
    return this.reportModel.getText(textId);
  }
  public getLine(lineId: string): ReportLine {
    return this.reportModel.getLine(lineId);
  }
  public getImage(imageId: string): ReportImage {
    return this.reportModel.getImage(imageId);
  }
  public getRectangle(rectangleId: string): ReportRectangle {
    return this.reportModel.getRectangle(rectangleId);
  }
  public getSummary(summaryId: string): ReportSummary {
    return this.reportModel.getSummary(summaryId);
  }
  public getDataPlate(dataPlateId: string): ReportDataPlateOffline {
    return this.reportModel.getDataPlate(dataPlateId);
  }

  public getPaperHeightPx(): number {
    return this.reportModel.getPaperHeightPx();
  }
  public saveReportTemplate(): Observable<ReportTemplate> {
    const reportTemplateUpdate = new ReportTemplateUpdate();
    reportTemplateUpdate.id = this.tempTemplate.id;
    reportTemplateUpdate.name = this.tempTemplate.name;
    reportTemplateUpdate.subjectModelIds = this.tempTemplate.subjectModelIds;
    reportTemplateUpdate.template = this.getTemplate();
    reportTemplateUpdate.isValid = this.tempTemplate.isValid;
    reportTemplateUpdate.reportTitleFormat = this.tempTemplate.reportTitleFormat;
    reportTemplateUpdate.reportTitleInputMode = this.tempTemplate.reportTitleInputMode;
    reportTemplateUpdate.schemaVersion = this.tempTemplate.schemaVersion;
    return this.reportTemplateService.updateReportTemplate(reportTemplateUpdate);
  }
  public getTemplate(): TemplateDefinition {
    const model = this.reportModel;
    const tempTemplate: TemplateDefinition = JSON.parse(JSON.stringify(this.tempTemplate.template, undefined, 4));
    const tempHeader: TemplateCommonSection = tempTemplate.header;
    const headerTexts: { [elementId: string]: ReportText } = model.getHeaderTexts();
    tempHeader.texts = [];
    for (const id of Object.keys(headerTexts)) {
      tempHeader.texts.push(headerTexts[id].getTemplateJSON());
    }
    const headerImages: { [elementId: string]: ReportImage } = model.getHeaderImages();
    tempHeader.images = [];
    for (const id of Object.keys(headerImages)) {
      tempHeader.images.push(headerImages[id].getTemplateJSON());
    }
    const headerLines: { [elementId: string]: ReportLine } = model.getHeaderLines();
    tempHeader.lines = [];
    for (const id of Object.keys(headerLines)) {
      tempHeader.lines.push(headerLines[id].getTemplateJSON());
    }
    const headerRectangles: { [elementId: string]: ReportRectangle } = model.getHeaderRectangles();
    tempHeader.rectangles = [];
    for (const id of Object.keys(headerRectangles)) {
      tempHeader.rectangles.push(headerRectangles[id].getTemplateJSON());
    }

    const tempBodies: TemplatePageSection[] = tempTemplate.bodies;
    tempBodies.length = 0;
    for (const id of model.getPageIds()) {
      const tempPage: TemplatePageSection = model.getPage(id).getTemplateJSON();
      const bodyTextKeys: string[] = this.getTextIds(id, 'Body');
      tempPage.texts = [];
      for (const textId of bodyTextKeys) {
        tempPage.texts.push(this.getText(textId).getTemplateJSON());
      }
      const bodyLineKeys = this.getLineIds(id, 'Body');
      tempPage.lines = [];
      for (const lineId of bodyLineKeys) {
        tempPage.lines.push(this.getLine(lineId).getTemplateJSON());
      }
      const bodyImageKeys = this.getImageIds(id, 'Body');
      tempPage.images = [];
      for (const imageId of bodyImageKeys) {
        tempPage.images.push(this.getImage(imageId).getTemplateJSON());
      }
      const bodyRectangleKeys = this.getRectangleIds(id, 'Body');
      tempPage.rectangles = [];
      for (const rectangleId of bodyRectangleKeys) {
        tempPage.rectangles.push(this.getRectangle(rectangleId).getTemplateJSON());
      }

      tempBodies.push(tempPage);
    }

    const tempFooter: TemplateCommonSection = tempTemplate.footer;
    const footerTexts: { [elementId: string]: ReportText } = model.getFooterTexts();
    tempFooter.texts = [];
    for (const id of Object.keys(footerTexts)) {
      tempFooter.texts.push(footerTexts[id].getTemplateJSON());
    }
    const footerImages: { [elementId: string]: ReportImage } = model.getFooterImages();
    tempFooter.images = [];
    for (const id of Object.keys(footerImages)) {
      tempFooter.images.push(footerImages[id].getTemplateJSON());
    }
    const footerLines: { [elementId: string]: ReportLine } = model.getFooterLines();
    tempFooter.lines = [];
    for (const id of Object.keys(footerLines)) {
      tempFooter.lines.push(footerLines[id].getTemplateJSON());
    }
    const footerRectangles: { [elementId: string]: ReportRectangle } = model.getFooterRectangles();
    tempFooter.rectangles = [];
    for (const id of Object.keys(footerRectangles)) {
      tempFooter.rectangles.push(footerRectangles[id].getTemplateJSON());
    }

    return tempTemplate;
  }
}
